/**
 * Closed lists shared by the database (CHECK constraints), the API (zod) and
 * the screens. Adding a value means a migration, which is the point.
 */

/** Booking site and customer app languages (WEB-4). */
export const LOCALES = ['it', 'en', 'de'] as const
export type Locale = (typeof LOCALES)[number]

/** The staff app's languages (PRD: Italian and English). */
export const STAFF_LOCALES = ['it', 'en'] as const
export type StaffLocale = (typeof STAFF_LOCALES)[number]

/**
 * S9 (Q-51): the shortest password a person may choose, and the one-time
 * passwords someone else chose are longer still. The API refuses a shorter
 * one; the staff app says so before asking.
 */
export const STAFF_PASSWORD_MIN_LENGTH = 12

/**
 * What the server checks on staff requests (ADM-1). Which role holds which is
 * data (role_permissions), so roles change without a release; the list of
 * things that can be permitted is code, because code checks them.
 */
export const PERMISSIONS = [
  'today.read',
  'bookings.read',
  'devices.manage',
  'users.manage',
  // Day 7: phone and walk-in bookings, the fleet and the customer records.
  'bookings.create',
  'prices.override',
  'customers.read',
  'fleet.read',
  'fleet.manage',
  // Day 8: the counter hands vehicles over and takes them back (OPS-1, OPS-2),
  // records what was paid at the counter (PAY-1), and the Owner lets a vehicle
  // go with a check failing (OPS-4).
  'rentals.manage',
  'payments.record',
  'checkout.override',
  // S2: the Sistema screen (backups, the worker, undelivered messages, alerts),
  // and with it the alert e-mails: whoever holds it is told when something breaks.
  'system.view',
  // S3: a booking cancelled or marked a no-show (RES-2, RES-5), and money given
  // back (PAY-3). Who may refund is open (Q-75): the Owner only, by default.
  'bookings.cancel',
  'payments.refund',
  // S5: the money reports and their CSVs (RPT-1, RPT-3, RPT-4), the check against
  // Stripe, and with them the Owner's morning and dispute e-mails.
  'reports.view',
  // S6 (CMP-2): the fines register: a notice matched to its rental, the letter
  // naming the renter, the communication to the authority and the admin fee.
  'fines.manage',
  // Timeline drag-to-reschedule: moving a booking's dates or vehicle. The Owner
  // and the Agent hold it; the Runner does not.
  'bookings.reschedule',
  // OPS-1: the rental terms' wording (Regole del contratto): a new version
  // written in the app, and the one in force chosen with the Owner's
  // authenticator code. The Owner alone.
  'terms.manage',
  // S7 (NTF-3): the operational alerts by e-mail, as they happen: a vehicle not
  // back on time, a card hold about to lapse or lapsed with the vehicle out, a
  // CaRGOS report that failed. Codes and plates only. The Owner by default; a
  // grant is data the Owner can change (Q-48).
  'operations.alerts',
  // S8 (CMP-4): correcting a customer's details and licences, as the privacy
  // notice promises. The Owner and the Agent: the counter fixes a typo.
  'customers.write',
  // S8 (CMP-4): the customer's privacy rights: a request to erase recorded and
  // withdrawn, a legal hold placed and lifted, a copy of their data exported,
  // and the records the law keeps after anonymisation opened. The Owner alone.
  'customers.privacy',
  // S9 (ADM-4): a future booking made on the old site, carried over by hand with
  // its reference, its agreed price and what was paid there. The Owner and the
  // Agent, who type them in before switch day.
  'bookings.carry_over',
  // S9 (LOC-5, J8): who takes a car to the customer or fetches it back, where they
  // meet and on which ferry. The Owner and the Agent assign the deliveries.
  'deliveries.manage',
  // S9 (Q-62): a delivery worked at the quay or the hotel by the person it is
  // assigned to: its booking, the hand-over, the return, the money and the
  // deposit, for that booking only and for a day after (staff/routes.ts). The Runner.
  'deliveries.work',
  // W2-S S1: the rate calendar (Prezzi): each category's price, minimum days and
  // stop sale for each day, one cell or many at once, the many with the Owner's
  // code. The Owner alone; a grant is data the Owner can change (Q-48).
  'prices.manage',
  // S11 (A1): a rental kept on paper while the server was down, typed back in at
  // its real times with the Owner's code. The Owner alone; a grant is data the
  // Owner can change (Q-48).
  'rentals.reenter',
  // Q-157 (the owner, 1 and 2 Nov): which models stand for each category, which
  // leads it, and their photos, shown in the booking results. The Owner and the
  // Agents.
  'models.manage',
] as const
export type Permission = (typeof PERMISSIONS)[number]

/**
 * The grants this build knows, in the order given; any other code is left out.
 * A database a later release has migrated can hold a grant an older build does
 * not know (0051's prices.manage, after a rollback): left in, it would fail the
 * session's schema and answer the person with a 500 (DECISIONS, 30 Oct).
 */
export function knownPermissions(codes: readonly string[]): Permission[] {
  const known = new Set<string>(PERMISSIONS)
  return codes.filter((code): code is Permission => known.has(code))
}

/**
 * S9 (LOC-5): the two trips a booking with a delivery zone can need: the car
 * taken to the customer (its pick-up is a zone), and fetched back (its return
 * is). One task per booking and leg (migration 0049).
 */
export const DELIVERY_LEGS = ['delivery', 'collection'] as const
export type DeliveryLeg = (typeof DELIVERY_LEGS)[number]

/**
 * PAY-1 while the card reader is PENDING: how a payment taken at the counter
 * was made, recorded by hand. The fiscal document is still issued the way the
 * shop issues it today.
 */
export const COUNTER_PAYMENT_METHODS = ['cash', 'card_terminal', 'bank_transfer'] as const
export type CounterPaymentMethod = (typeof COUNTER_PAYMENT_METHODS)[number]

/**
 * Every way money arrives. `online` (WEB-2: Stripe's hosted Checkout) is
 * recorded only by Stripe's signed webhook, never typed at the counter.
 * `deposit` (PAY-3) is the part of a security deposit kept with a reason,
 * recorded when it is captured, never typed as a payment. `old_site` (S9,
 * ADM-4) is what a booking carried over from the old site was paid there,
 * once, with Stripe's id when it has one: kept apart from the tills and the
 * fiscal documents (Q-120), since none was taken here.
 */
export const PAYMENT_METHODS = [
  ...COUNTER_PAYMENT_METHODS,
  'online',
  'deposit',
  'old_site',
] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

/**
 * PAY-2: how a security deposit is taken at the counter. `reader`: a card
 * hold on Stripe's smart reader (a manual-capture PaymentIntent). `pos`: a
 * hold made on the shop's own card terminal and recorded by hand, the fallback
 * while no reader is set up. `cash`: notes kept in the till, where the
 * category accepts them (an open decision, so a setting per category).
 */
export const DEPOSIT_METHODS = ['reader', 'pos', 'cash'] as const
export type DepositMethod = (typeof DEPOSIT_METHODS)[number]

/**
 * `pending`: on the reader, waiting for the card. `held`: the money is held.
 * Then one end, and only one: `released` (all of it let go), `captured` (part
 * or all kept as a payment, the rest let go), `expired` (the card network let
 * the hold lapse), or `failed` (no money was ever held: declined, cancelled).
 */
export const DEPOSIT_STATES = [
  'pending',
  'held',
  'released',
  'captured',
  'expired',
  'failed',
] as const
export type DepositState = (typeof DEPOSIT_STATES)[number]

/** PAY-2: a debit or prepaid card's hold blocks the customer's own money, which staff say. */
export const CARD_FUNDINGS = ['credit', 'debit', 'prepaid', 'unknown'] as const
export type CardFunding = (typeof CARD_FUNDINGS)[number]

/**
 * WEB-2: a payment page hosted by the provider (Stripe Checkout) for one
 * booking: `open` until paid or expired; `processing` while a delayed
 * payment method settles. ("Checkout" alone means the hand-over here.)
 */
export const PAYMENT_PAGE_STATES = ['open', 'processing', 'paid', 'failed', 'expired'] as const
export type PaymentPageState = (typeof PAYMENT_PAGE_STATES)[number]

/**
 * A page that can still take money: at most one per booking (the unique
 * index `stripe_checkout_sessions_one_live` names the same two).
 */
export const LIVE_PAYMENT_PAGE_STATES = [
  'open',
  'processing',
] as const satisfies readonly PaymentPageState[]

/** Where the customer opened the page: the website, or the customer app (Stripe's `origin_context`). */
export const PAYMENT_PAGE_ORIGINS = ['web', 'mobile_app'] as const
export type PaymentPageOrigin = (typeof PAYMENT_PAGE_ORIGINS)[number]

/**
 * RES-5: where a cancelled or no-show booking's fee came from. `policy`: the
 * terms it was made under, as computed; `override`: lowered from that, with a
 * reason, by someone who may change prices; `typed`: no policy in its terms
 * (Q-27), so the counter typed what is kept; `legacy`: ended before S3, kept
 * at the booking's total so no balance moved.
 */
export const CANCELLATION_FEE_RULES = ['policy', 'override', 'typed', 'legacy'] as const
export type CancellationFeeRule = (typeof CANCELLATION_FEE_RULES)[number]

/** Why a booking cannot end so now: not confirmed, its pick-up not yet due, a deposit held. */
export const CANCELLATION_BLOCKS = ['status', 'too_early', 'deposit'] as const
export type CancellationBlock = (typeof CANCELLATION_BLOCKS)[number]

/** Why a returned booking cannot be closed yet (Q-13); S5: a dispute still open. */
export const CLOSE_BLOCKS = ['status', 'balance', 'deposit', 'refund', 'dispute'] as const
export type CloseBlock = (typeof CLOSE_BLOCKS)[number]

/**
 * PAY-3: how money goes back. `stripe`: through Stripe, to the card or method
 * that paid (an online payment, or a deposit kept on the reader). The others
 * are done at the counter or the bank and recorded by hand, as payments are.
 */
export const REFUND_METHODS = ['cash', 'card_terminal', 'bank_transfer', 'stripe'] as const
export type RefundMethod = (typeof REFUND_METHODS)[number]

/**
 * Stripe's own refund statuses. A refund by hand is `succeeded` from the
 * start. Through Stripe it starts `pending`, and even a `succeeded` one can
 * later fail or be cancelled from the Dashboard (Stripe's docs).
 */
export const REFUND_STATES = [
  'pending',
  'requires_action',
  'succeeded',
  'failed',
  'canceled',
] as const
export type RefundState = (typeof REFUND_STATES)[number]

/** The refunds that count against what is owed back: money gone, or on its way. */
export const LIVE_REFUND_STATES = [
  'pending',
  'requires_action',
  'succeeded',
] as const satisfies readonly RefundState[]

/**
 * S5 (PAY-6): cash moving through a pick-up point's drawer, each against the
 * one row it moves. In: a cash payment, a cash deposit taken. Out: a cash
 * refund, the part of a cash deposit given back (all of it, or what was not
 * kept). The part of a deposit kept never moves: it is already in the drawer.
 */
export const CASH_MOVEMENT_KINDS = [
  'payment_in',
  'deposit_in',
  'refund_out',
  'deposit_out',
] as const
export type CashMovementKind = (typeof CASH_MOVEMENT_KINDS)[number]

/** The movements that bring cash in; the others take it out (migration 0029 sums them so). */
export const CASH_IN_KINDS = [
  'payment_in',
  'deposit_in',
] as const satisfies readonly CashMovementKind[]

/**
 * S5c: what the night's check against Stripe could neither match nor repair
 * by itself, for a person to read (migration 0030).
 * - `payment_unrecorded`: a payment page of ours Stripe reports paid, which
 *   the app could not record.
 * - `amount_mismatch`: a payment recorded for another amount than Stripe took.
 * - `deposit_mismatch`: Stripe kept another part of a deposit than the app
 *   recorded. Deposits are never repaired: "Controlla con Stripe" on the booking.
 * - `refund_unrecorded`: money Stripe gave back that the app has not recorded
 *   and will not by itself (deposit money refunded in the Dashboard).
 * - `refund_status`: a refund the app holds in another status than Stripe's,
 *   one it cannot move to.
 * - `dispute_unrecorded`: a dispute the app could not record.
 * - `foreign_reference`: Stripe's metadata names a page, deposit or refund this
 *   database never made (another installation on the same Stripe account).
 * - `unexpected`: a movement of a kind this code does not know, or from an
 *   object it does not expect.
 * - `currency`: customer money not in euro.
 */
export const STRIPE_DIFFERENCE_KINDS = [
  'payment_unrecorded',
  'amount_mismatch',
  'deposit_mismatch',
  'refund_unrecorded',
  'refund_status',
  'dispute_unrecorded',
  'foreign_reference',
  'unexpected',
  'currency',
] as const
export type StripeDifferenceKind = (typeof STRIPE_DIFFERENCE_KINDS)[number]

/** S5c: what started a check against Stripe: the worker's nightly schedule, or the CLI. */
export const RECONCILE_TRIGGERS = ['schedule', 'cli'] as const
export type ReconcileTrigger = (typeof RECONCILE_TRIGGERS)[number]

/**
 * CMP-1 (S6): whose identity a hand-over records. `main` is the renter who
 * signs. `second` is a second driver: the record carries one, but only tests
 * write it until a booking can name one (RES-3, S9).
 */
export const HANDOVER_IDENTITY_ROLES = ['main', 'second'] as const
export type HandoverIdentityRole = (typeof HANDOVER_IDENTITY_ROLES)[number]

/**
 * Decision 5 (S6): where the home address came from. Read off a document
 * that prints it, or declared by the renter: passports and many licences
 * print no address.
 */
export const ADDRESS_SOURCES = ['document', 'declared'] as const
export type AddressSource = (typeof ADDRESS_SOURCES)[number]

/**
 * CMP-1 (S6): how a contract reached CaRGOS. `api`: sent by the app (the
 * worker). `portal` and `pec`: reported by hand on the portal or by PEC, and
 * the receipt typed.
 */
export const CARGOS_CHANNELS = ['api', 'portal', 'pec'] as const
export type CargosChannel = (typeof CARGOS_CHANNELS)[number]

/** `check`: CaRGOS's own dry run (`api/Check`), never a report. `send`: `api/Send`. */
export const CARGOS_MODES = ['check', 'send'] as const
export type CargosMode = (typeof CARGOS_MODES)[number]

/**
 * What became of a contract for CaRGOS.
 * - Sent by the app: `queued`, then one of:
 *   - `checked`: the dry run passed. Never a report.
 *   - `sent`: CaRGOS's receipt.
 *   - `refused`: CaRGOS answered with an error.
 *   - `unknown`: it may have reached CaRGOS. The counter checks the portal, and
 *     nothing sends it again by itself.
 *   - `cancelled`: a receipt typed while it waited.
 * - Typed from the portal or the PEC: `recorded`.
 */
export const CARGOS_TRANSMISSION_STATUSES = [
  'queued',
  'checked',
  'sent',
  'refused',
  'unknown',
  'cancelled',
  'recorded',
] as const
export type CargosTransmissionStatus = (typeof CARGOS_TRANSMISSION_STATUSES)[number]

/**
 * A report waiting to be sent, or one that may have reached CaRGOS without an
 * answer: the identity it carries is kept until it is settled (S8; migration
 * 0045 checks the same two).
 */
export const PENDING_CARGOS_STATUSES = [
  'queued',
  'unknown',
] as const satisfies readonly CargosTransmissionStatus[]

/**
 * CMP-3 (S6, decision 3): the fiscal document a payment or a refund was given,
 * as the point's till (RT) or someone else issued it. The app records what was
 * issued; it issues nothing, and never builds an SDI pipeline.
 * - For money taken: `receipt` (documento commerciale) or `invoice` (fattura).
 * - For money given back: `return` (reso) or `credit_note` (nota di credito).
 */
export const FISCAL_DOCUMENT_KINDS = ['receipt', 'return', 'invoice', 'credit_note'] as const
export type FiscalDocumentKind = (typeof FISCAL_DOCUMENT_KINDS)[number]
export const PAYMENT_DOCUMENT_KINDS = ['receipt', 'invoice'] as const
export const REFUND_DOCUMENT_KINDS = ['return', 'credit_note'] as const
/** The till issues receipts and returns only: an invoice never counts in its daily closure. */
export const TILL_DOCUMENT_KINDS = ['receipt', 'return'] as const

/**
 * S9 (Q-120, the accountant's to confirm): money that gets no fiscal document
 * here, nor does what goes back from it: what a booking carried over from the
 * old site was paid there. Fiscale's list and the booking's lines read this;
 * the database refuses such a document too (migration 0048).
 */
export const PAYMENT_METHODS_WITHOUT_FISCAL_DOCUMENT = [
  'old_site',
] as const satisfies readonly PaymentMethod[]

/**
 * S9 (the accountant's to confirm): money Ricavi (RPT-1) leaves out, with what
 * goes back from it and what a bank takes back of it: what a booking carried
 * over from the old site was paid there, before the switch. A separate list
 * from the one above: the two answers may differ.
 */
export const PAYMENT_METHODS_OUTSIDE_REVENUE = [
  'old_site',
] as const satisfies readonly PaymentMethod[]

/** Whether money paid this way, and what goes back from it, gets a fiscal document here. */
export function fiscalDocumentHere(method: PaymentMethod): boolean {
  return !(PAYMENT_METHODS_WITHOUT_FISCAL_DOCUMENT as readonly PaymentMethod[]).includes(method)
}

/**
 * Who issued it: the point's telematic till (`till`), a provider (none is
 * chosen yet: PENDING), or anyone else (`other`: an accountant's invoice).
 */
export const FISCAL_SOURCES = ['till', 'provider', 'other'] as const
export type FiscalSource = (typeof FISCAL_SOURCES)[number]

/** A report CaRGOS has: it lets the car leave, and the ID number is erased (decisions 1 and 2). */
export const CARGOS_RECEIPT_STATUSES = [
  'sent',
  'recorded',
] as const satisfies readonly CargosTransmissionStatus[]

/**
 * OPS-3: extra charges at return. Fuel is computed; the others are typed with
 * a reason. `fine_fee` (S6, CMP-2): the admin fee for a traffic fine, charged
 * from the fines register at the amount the rental was handed over with,
 * never typed at the return.
 */
export const RENTAL_CHARGE_KINDS = [
  'fuel',
  'late',
  'cleaning',
  'damage',
  'other',
  'fine_fee',
  'excess_km',
] as const
export type RentalChargeKind = (typeof RENTAL_CHARGE_KINDS)[number]

/**
 * CMP-2 (S6): a traffic fine notified to the company. `registered`: the
 * notice typed; `matched`: the rental that had the car named; `communicated`:
 * the renter's data sent to the authority; `closed`: nothing more to do (the
 * fee charged, or decided against); `not_ours`: no rental of ours had the car
 * then; `withdrawn`: the authority withdrew the notice.
 */
export const FINE_STATUSES = [
  'registered',
  'matched',
  'communicated',
  'closed',
  'not_ours',
  'withdrawn',
] as const
export type FineStatus = (typeof FINE_STATUSES)[number]

/** How the rental was found: the only one that had the car then, or picked by the Owner. */
export const FINE_MATCH_KINDS = ['auto', 'manual'] as const
export type FineMatchKind = (typeof FINE_MATCH_KINDS)[number]

/** How the renter's data reached the authority. */
export const FINE_CHANNELS = ['pec', 'form', 'portal', 'post', 'other'] as const
export type FineChannel = (typeof FINE_CHANNELS)[number]

/** Fuel is read off the gauge in eighths: 0 empty, 8 full. */
export const FUEL_EIGHTHS = 8

/** How a staff session began: a password proves more than a PIN (PIN changes need it). */
export const STAFF_SESSION_METHODS = ['password', 'pin'] as const
export type StaffSessionMethod = (typeof STAFF_SESSION_METHODS)[number]

export const STAFF_SESSION_END_REASONS = [
  'locked',
  'replaced',
  'device_revoked',
  'user_disabled',
  // CTR-2: the iPad was turned to the customer to sign; staff unlock with a PIN again.
  'signing',
] as const
export type StaffSessionEndReason = (typeof STAFF_SESSION_END_REASONS)[number]

/** FLT-1. */
export const VEHICLE_KINDS = ['car', 'van', 'minivan', 'scooter', 'motorcycle', 'ebike'] as const
export type VehicleKind = (typeof VEHICLE_KINDS)[number]

export const FUEL_TYPES = ['petrol', 'diesel', 'electric', 'hybrid', 'lpg', 'methane'] as const
export type FuelType = (typeof FUEL_TYPES)[number]

export const TRANSMISSIONS = ['manual', 'automatic'] as const
export type Transmission = (typeof TRANSMISSIONS)[number]

/** Administrative only; whether a vehicle is out on rent is derived from its allocations. */
export const VEHICLE_STATUSES = ['active', 'retired'] as const
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number]

/** FLT-3. */
export const VEHICLE_DOCUMENT_KINDS = ['insurance', 'revisione', 'bollo', 'other'] as const
export type VehicleDocumentKind = (typeof VEHICLE_DOCUMENT_KINDS)[number]

/** FLT-4. A block takes a vehicle out of service without a workshop reason. */
/**
 * FLT-5: what a vehicle is doing right now, derived from its allocations and
 * never stored, so rentals, maintenance and turnarounds set it by themselves.
 * `turnaround` is the cleaning buffer after a rental (AVL-3).
 */
export const VEHICLE_STATES = [
  'available',
  'reserved',
  'on_rent',
  'turnaround',
  'maintenance',
  'blocked',
  'retired',
] as const
export type VehicleState = (typeof VEHICLE_STATES)[number]

/** RES-1: the sources staff book for; `web` is the website's own. */
export const STAFF_BOOKING_SOURCES = ['walk_in', 'phone', 'whatsapp', 'email'] as const
export type StaffBookingSource = (typeof STAFF_BOOKING_SOURCES)[number]

export const MAINTENANCE_KINDS = ['service', 'repair', 'block'] as const
export type MaintenanceKind = (typeof MAINTENANCE_KINDS)[number]

/** ARCHITECTURE, availability engine. */
export const ALLOCATION_KINDS = ['reservation', 'rental', 'maintenance', 'block', 'buffer'] as const
export type AllocationKind = (typeof ALLOCATION_KINDS)[number]

/**
 * RES-1 exactly; "app" waits for open question Q-08. `old_site` (S9, ADM-4): a
 * booking made on the old site, carried over by hand with its reference.
 */
export const RESERVATION_SOURCES = [
  'walk_in',
  'phone',
  'whatsapp',
  'email',
  'web',
  'old_site',
] as const
export type ReservationSource = (typeof RESERVATION_SOURCES)[number]

/**
 * S9 (ADM-4, the owner's answer of 29 Oct): bookings sent no automatic e-mail
 * at all, neither confirmation, reminder nor notice: one carried over from
 * the old site, whose customer knows it by the old site's reference and has
 * never seen its code here. The counter writes to them by hand.
 */
export const SOURCES_WITHOUT_AUTOMATIC_EMAIL = [
  'old_site',
] as const satisfies readonly ReservationSource[]

export function sendsAutomaticEmail(source: ReservationSource): boolean {
  return !(SOURCES_WITHOUT_AUTOMATIC_EMAIL as readonly ReservationSource[]).includes(source)
}

/** LOC-2. */
export const DELIVERY_ZONE_KINDS = ['port', 'hotel'] as const
export type DeliveryZoneKind = (typeof DELIVERY_ZONE_KINDS)[number]

/** Who caused a change, for the audit log. `public` is an anonymous website visitor. */
export const ACTOR_KINDS = ['staff', 'customer', 'system', 'public'] as const
export type ActorKind = (typeof ACTOR_KINDS)[number]

/** Customer-facing text in every site language. */
export type LocalizedText = Record<Locale, string>

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type Weekday = (typeof WEEKDAYS)[number]

/** LOC-1. Europe/Rome wall-clock ranges per weekday, "HH:MM"; a missing day is closed. */
export type OpeningHours = Partial<Record<Weekday, Array<[string, string]>>>

/**
 * One category on a driving licence, with the date it was FIRST obtained
 * (field 10 of an EU licence): "held for 3 years" counts from there, not from
 * the date the current card was issued.
 */
export interface LicenceCategory {
  code: string
  firstIssuedOn: string | null
}

/**
 * ARCHITECTURE: private buckets; identity documents get shorter links and
 * logged views. `contracts` holds the signed contract PDFs (CTR-1, CTR-5),
 * written by the worker, never uploaded. `compliance` (S6, CMP-2) holds the
 * letters naming a fine's renter, written by the worker, never uploaded.
 */
export const MEDIA_BUCKETS = [
  'identity-docs',
  'vehicle-media',
  'contracts',
  'compliance',
  // Q-157: a model's photo taken in the staff app, shown on the website.
  'model-photos',
] as const
export type MediaBucket = (typeof MEDIA_BUCKETS)[number]

/** What an upload may contain, checked against the file's first bytes, not just its label. */
export const MEDIA_CONTENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const
export type MediaContentType = (typeof MEDIA_CONTENT_TYPES)[number]

export const MEDIA_STATUSES = ['pending', 'stored'] as const
export type MediaStatus = (typeof MEDIA_STATUSES)[number]

/**
 * S8: a file's row may also be `erased`: its bytes removed for privacy, the
 * row kept with when. Only the database and the privacy code read this list.
 * Every API shows MEDIA_STATUSES, so the frozen booking site's (D0) never
 * changes: an erased file is left out of it, or named apart.
 */
export const MEDIA_ROW_STATUSES = [...MEDIA_STATUSES, 'erased'] as const
export type MediaRowStatus = (typeof MEDIA_ROW_STATUSES)[number]

/**
 * S8: why the Owner keeps a customer's data past its time (a legal hold). A
 * closed list: staff are asked for a reason, not a story (S3).
 */
export const LEGAL_HOLD_REASONS = [
  'litigation',
  'authority_request',
  'insurance_claim',
  'other',
] as const
export type LegalHoldReason = (typeof LEGAL_HOLD_REASONS)[number]

/** S8: how a customer's request to erase their data reached the company. */
export const ERASURE_REQUEST_CHANNELS = ['email', 'letter', 'in_person', 'phone'] as const
export type ErasureRequestChannel = (typeof ERASURE_REQUEST_CHANNELS)[number]

/**
 * S8: why a customer's data is handed to them: a copy of it (access, art. 15
 * GDPR) or to take it elsewhere (portability, art. 20). The file is the same;
 * the audit row keeps which was asked.
 */
export const DATA_EXPORT_PURPOSES = ['access', 'portability'] as const
export type DataExportPurpose = (typeof DATA_EXPORT_PURPOSES)[number]

/** CUS-2. */
export const DOCUMENT_KINDS = ['id_card', 'passport', 'driving_licence', 'other'] as const
export type DocumentKind = (typeof DOCUMENT_KINDS)[number]

export const DOCUMENT_SIDES = ['front', 'back', 'single'] as const
export type DocumentSide = (typeof DOCUMENT_SIDES)[number]

/**
 * CTR-1: a contract is prepared from the booking (a frozen snapshot), signed
 * once on the counter iPad, or voided: replaced by a newer one, or no longer
 * matching the booking. A signed contract is never edited.
 */
export const CONTRACT_STATUSES = ['prepared', 'signed', 'void'] as const
export type ContractStatus = (typeof CONTRACT_STATUSES)[number]

export const CONTRACT_VOID_REASONS = ['superseded', 'booking_changed'] as const
export type ContractVoidReason = (typeof CONTRACT_VOID_REASONS)[number]

/** CTR-2 and CTR-3: the contract's own signature, and the one under the clauses of art. 1341–1342 c.c. */
export const CONTRACT_SIGNATURE_KINDS = ['main', 'specific_approval'] as const
export type ContractSignatureKind = (typeof CONTRACT_SIGNATURE_KINDS)[number]

/**
 * S1b. What the backup container records: a base backup, its verification, a
 * pass copying uploaded files, a change in WAL archiving, and "pending" while
 * no bucket is configured (BACKUP_MODE=off).
 */
export const BACKUP_RUN_KINDS = [
  'base',
  'verify',
  'files',
  'archiver',
  'pending',
  // S2: every 15 minutes while the container runs, a long base backup included.
  'heartbeat',
] as const
export type BackupRunKind = (typeof BACKUP_RUN_KINDS)[number]

/** S2. What writes a heartbeat row; the backup container's go to ops_backup_runs. */
export const HEARTBEAT_COMPONENTS = ['worker'] as const
export type HeartbeatComponent = (typeof HEARTBEAT_COMPONENTS)[number]

/**
 * S2. What the alerts watch (docs/RUNBOOK.md, alerts): each is evaluated every
 * five minutes, fires after two bad evaluations in a row and resolves after
 * two good ones; `outbox.dead` is a one-shot notice of new dead letters.
 */
export const ALERT_KEYS = [
  'backups.base_stale',
  'backups.verify_failed',
  'backups.files_failed',
  'backups.archiving_failing',
  'backups.silent',
  'outbox.overdue',
  'outbox.dead',
  'disk.low',
  // S8: a file erased for privacy still on disk, or its backup copy not yet overwritten.
  'privacy.erasures_stuck',
] as const
export type AlertKey = (typeof ALERT_KEYS)[number]

export const ALERT_STATES = ['ok', 'firing'] as const
export type AlertState = (typeof ALERT_STATES)[number]
